import React from 'react';
import {SortableHeaderContent, Table, TableCell, TableRow, TableWrapper} from './ResponsiveTable.styled';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';


export type SortOrder = 'asc' | 'desc';

export type ResponsiveTableColumn<T> = {
    id: string;
    header: React.ReactNode;
    headerLabel?: string;
    render: (row: T) => React.ReactNode;
    cellClassName?: string | ((row: T) => string | undefined);
    sortable?: boolean;
    sortKey?: string;
};

export type ResponsiveTableProps<T> = {
    columns: ResponsiveTableColumn<T>[];
    data: T[];
    getRowKey: (row: T, index: number) => React.Key;
    emptyState?: React.ReactNode;
    className?: string;
    ariaLabel?: string;
    sortColumn?: string | null;
    sortOrder?: SortOrder;
    onSortChange?: (sortKey: string) => void;
    getRowClassName?: (row: T) => string | undefined;
};

function headerToLabel(header: React.ReactNode): string | undefined {
    if (typeof header === 'string') return header;
    return undefined;
}

export default function ResponsiveTable<T>(props: ResponsiveTableProps<T>) {
    const { columns, data, getRowKey, emptyState, className, ariaLabel, sortColumn, sortOrder, onSortChange, getRowClassName } = props;

    const getSortIcon = (col: ResponsiveTableColumn<T>) => {
        if (!col.sortable) return null;

        const key = col.sortKey ?? col.id;

        if (sortColumn !== key) return <UnfoldMoreIcon fontSize="small" />;

        return sortOrder === 'asc'
            ? <KeyboardArrowUpIcon fontSize="small" />
            : <KeyboardArrowDownIcon fontSize="small" />;
    };

    const handleHeaderClick = (col: ResponsiveTableColumn<T>) => {
        if (!col.sortable || !onSortChange)
            return;

        onSortChange(col.sortKey ?? col.id);
    };

    return (
        <TableWrapper className={className}>
            <Table role="table" aria-label={ariaLabel}>
                <thead>
                    <tr role="row">
                        {columns.map(col => {
                            const key = col.sortKey ?? col.id;
                            const isActive = sortColumn === key;

                            return (
                                <TableCell
                                    key={col.id}
                                    role="columnheader"
                                    scope="col"
                                    $sortable={col.sortable}
                                    $active={isActive}
                                    onClick={() => handleHeaderClick(col)}
                                    aria-sort={
                                        !col.sortable ? undefined
                                            : isActive
                                                ? sortOrder === 'asc' ? 'ascending' : 'descending'
                                                : 'none'
                                    }
                                >
                                    <SortableHeaderContent>
                                        {col.header}
                                        {getSortIcon(col)}
                                    </SortableHeaderContent>
                                </TableCell>
                            )
                        })}
                    </tr>
                </thead>
                <tbody>
                    {data.length === 0 && (
                        <TableRow key="empty" role="row">
                            <TableCell className="empty-cell" data-label="" colSpan={columns.length} role="cell">
                                {emptyState ?? null}
                            </TableCell>
                        </TableRow>
                    )}
                    {data.map((row, rowIndex) => (
                        <TableRow key={getRowKey(row, rowIndex)} role="row" className={getRowClassName?.(row)}>
                            {columns.map(col => {
                                const label = col.headerLabel ?? headerToLabel(col.header) ?? '';

                                const className =
                                    typeof col.cellClassName === 'function'
                                        ? col.cellClassName(row)
                                        : col.cellClassName;

                                return (
                                    <TableCell
                                        key={col.id}
                                        data-label={label}
                                        className={className}
                                        role="cell"
                                    >
                                        {col.render(row)}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    ))}
                </tbody>
            </Table>
        </TableWrapper>
    );
}
